import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { api } from "./api.ts";

export type Announcement = components["schemas"]["AnnouncementDetails"];
export type AnnouncementAudience = components["schemas"]["AnnouncementAudience"];
export type AnnouncementStatus = components["schemas"]["AnnouncementStatus"];
export type AnnouncementPreview = components["schemas"]["AnnouncementPreview"];

export const announcementKeys = {
  all: ["announcements"] as const,
  list: (filters: object) => ["announcements", "list", filters] as const,
  preview: (target: object) => ["announcements", "preview", target] as const,
};

/** Audiences in the order the composer offers them. */
export const audiences: readonly AnnouncementAudience[] = ["AllParents", "Grades", "Classes", "Teachers", "Staff"];

/** Grades a class can have (Academic's course limits, 5 to 12). */
export const grades: readonly number[] = [5, 6, 7, 8, 9, 10, 11, 12];

/** Server limits from Domain/Announcement.cs, mirrored so the form stops typing at the same length. */
export const TITLE_MAX_LENGTH = 150;
export const TEXT_MAX_LENGTH = 3000;
export const MAX_CLASSES = 50;

export const statusColor: Record<AnnouncementStatus, string> = {
  Scheduled: "blue",
  Sent: "green",
  Cancelled: "default",
};

/** The composer's audience and schedule, as the preview and send requests take them. */
export interface AnnouncementTarget {
  audience: AnnouncementAudience | undefined;
  grades: number[];
  classIds: string[];
  scheduledAt: string | undefined;
}

/** True when the target names everything its audience needs, so the server can count it. */
export function isCompleteTarget(target: AnnouncementTarget): target is AnnouncementTarget & { audience: AnnouncementAudience } {
  if (!target.audience) return false;
  if (target.audience === "Grades") return target.grades.length > 0;
  if (target.audience === "Classes") return target.classIds.length > 0;
  return true;
}

/** The recipient count for the target from POST /announcements/preview; idle until the target is complete. */
export function useAnnouncementPreview(target: AnnouncementTarget, enabled: boolean) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: announcementKeys.preview(target),
    enabled: enabled && isCompleteTarget(target),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      if (!isCompleteTarget(target)) throw new Error(t("app.error"));
      const { data, error } = await api.POST("/announcements/preview", {
        body: { audience: target.audience, grades: target.grades, classIds: target.classIds, scheduledAt: target.scheduledAt ?? null },
      });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });
}
